import { useEffect, useRef } from 'react'
import type { NormalizedRun } from '../api/types'
import { useArenaStore } from '../store'
import type { Toast } from '../store'

/** Detect runs that just transitioned from running → completed/failed. */
export function detectTransitions(
  runs: readonly NormalizedRun[],
  prevStatuses: ReadonlyMap<string, string>,
): Array<Omit<Toast, 'id'>> {
  const out: Array<Omit<Toast, 'id'>> = []
  for (const run of runs) {
    const prev = prevStatuses.get(run.id)
    if (prev === 'running' && (run.status === 'completed' || run.status === 'failed')) {
      out.push({ runId: run.id, title: run.title, status: run.status })
    }
  }
  return out
}

export function Toasts() {
  const runs        = useArenaStore((s) => s.runs)
  const toasts      = useArenaStore((s) => s.toasts)
  const addToast    = useArenaStore((s) => s.addToast)
  const dismissToast = useArenaStore((s) => s.dismissToast)

  // Track previous run statuses for transition detection
  const prevStatusesRef = useRef<Map<string, string>>(new Map())

  useEffect(() => {
    const transitions = detectTransitions(runs, prevStatusesRef.current)
    for (const t of transitions) addToast(t)
    prevStatusesRef.current = new Map(runs.map((r) => [r.id, r.status]))
  }, [runs, addToast])

  // Per-toast 6s auto-dismiss timers
  const timerRefs = useRef(new Map<string, ReturnType<typeof setTimeout>>())

  useEffect(() => {
    // Cancel timers for toasts that were already dismissed
    for (const [id, timer] of timerRefs.current) {
      if (!toasts.find((t) => t.id === id)) {
        clearTimeout(timer)
        timerRefs.current.delete(id)
      }
    }
    // Start timers for newly arrived toasts
    for (const toast of toasts) {
      if (!timerRefs.current.has(toast.id)) {
        const id = toast.id
        const timer = setTimeout(() => {
          dismissToast(id)
          timerRefs.current.delete(id)
        }, 6000)
        timerRefs.current.set(id, timer)
      }
    }
  }, [toasts, dismissToast])

  if (toasts.length === 0) return null

  return (
    <div style={s.container} data-testid="toasts-container">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          style={{ ...s.card, ...(toast.status === 'failed' ? s.cardFailed : s.cardDone) }}
          data-testid="toast-card"
        >
          <span style={s.icon}>{toast.status === 'completed' ? '✓' : '✗'}</span>
          <span style={s.title}>{toast.title}</span>
          <span style={s.label}>{toast.status === 'completed' ? 'shipped' : 'failed'}</span>
          <button
            style={s.close}
            aria-label="Dismiss"
            onClick={() => dismissToast(toast.id)}
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  )
}

const s: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed',
    bottom: '1.5rem',
    right:  '1.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    zIndex: 40,
    alignItems: 'flex-end',
    pointerEvents: 'none',
  },
  card: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.6rem 0.875rem',
    borderRadius: '0.75rem',
    border: '1px solid',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
    fontFamily: "'Nunito', system-ui, sans-serif",
    fontSize: '0.82rem',
    fontWeight: 700,
    maxWidth: '20rem',
    pointerEvents: 'auto',
  },
  cardDone: {
    background: 'rgba(34,211,238,0.12)',
    borderColor: 'rgba(34,211,238,0.35)',
    color: '#22d3ee',
  },
  cardFailed: {
    background: 'rgba(248,113,113,0.12)',
    borderColor: 'rgba(248,113,113,0.35)',
    color: '#f87171',
  },
  icon: {
    fontSize: '0.9rem',
    flexShrink: 0,
  },
  title: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: '#f5ebe0',
    fontWeight: 700,
  },
  label: {
    opacity: 0.65,
    fontSize: '0.72rem',
    flexShrink: 0,
  },
  close: {
    background: 'none',
    border: 'none',
    color: 'inherit',
    opacity: 0.5,
    cursor: 'pointer',
    fontSize: '0.75rem',
    padding: '0 0.1rem',
    flexShrink: 0,
  },
}
